/**
 * Эрэмбийн нэг мөр + дэлгэгддэг задаргаа.
 *
 * ⚠️ САЛБАРЫН ажилтнууд нь ЗЭРЭГЛЭЛГҮЙ. Нэрийн цагаан толгойн
 *    дарааллаар (сервер тал `ORDER BY full_name`) ирнэ — дүнгээр
 *    эрэмбэлбэл дугааргүй ч де-факто зэрэглэл болно.
 *
 * ⚠️ Задаргааг өөр САЛБАРЫНХ бол сервер 403 буцаана. UI нь алдааг
 *    нуухгүй, шалтгааныг хэлнэ.
 */

import { useQuery } from '@tanstack/react-query';
import { formatMnt, formatQty } from '@boots/domain';
import { api, type Envelope } from '../../lib/api';
import { InfoTip } from '../info-tip';
import type { LeaderboardEntry, ScoreRow } from '../../lib/types';

interface Detail extends LeaderboardEntry {
  contributors: { id: string; name: string; revenue: string; units: number }[];
}

export function LeaderRow({
  entry, isBranch, mine, open, onToggle, competition, period,
  prevScore = null, prevTop = null, prevLabel = null,
}: {
  entry: LeaderboardEntry;
  isBranch: boolean;
  mine: boolean;
  open: boolean;
  onToggle: () => void;
  competition: string;
  period: string;
  /** Өмнөх хугацаанд ЭНЭ оролцогчийн авсан оноо. Байхгүй бол null. */
  prevScore?: number | null;
  /** Өмнөх хугацааны 1-р байр — «шилдгээс хэр хол вэ». */
  prevTop?: { name: string; score: number } | null;
  /** «2026 оны 8 сар» гэх мэт нэр — тайлбарт хэрэглэнэ. */
  prevLabel?: string | null;
}) {
  const move = rankMove(entry);

  /*
    Хоёр харьцуулалт. `changePercent`-ыг ашиглахгүй — тэр нь мөнгөний
    string хүлээдэг; энд оноо нь аль хэдийн тоо болсон байна.
  */
  const score = Number(entry.totalScore);
  const ownPct =
    prevScore === null || prevScore === 0 ? null : Math.round(((score - prevScore) / prevScore) * 1000) / 10;
  const vsTopPct =
    !prevTop || prevTop.score === 0
      ? null
      : Math.round(((score - prevTop.score) / prevTop.score) * 1000) / 10;

  const detail = useQuery({
    queryKey: ['leaderboard-detail', competition, period, entry.subject.id],
    queryFn: () =>
      api<Envelope<Detail>>(
        `/leaderboard/${competition}/${entry.subject.id}/breakdown?period=${period}`,
      ),
    enabled: open,
    staleTime: 60_000,
  });

  return (
    <li className={`border-b border-hair/70 ${mine ? 'bg-accent-100/40' : ''}`}>
      {/*
        ⚠️ «i» товч нь дэлгэх товчны ДОТОР байж БОЛОХГҮЙ (button дотор
           button бол хүчингүй HTML, гар ч, дэлгэцийн уншигч ч
           будилна). Тиймээс мөрийн гадна хүрээ нь `div`, дэлгэх нь
           дотроо бүтэн өргөнтэй товч.
      */}
      <div className="flex w-full items-center gap-2 px-2 transition hover:bg-row-hover">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex min-w-0 flex-1 items-center gap-3 py-2.5 text-left"
      >
        <span className="num w-6 shrink-0 text-right text-body text-ink-2">{entry.rank}</span>

        <span className="min-w-0 flex-1 text-body text-pretty">
          {entry.subject.name}
          {entry.meta?.isNew ? (
            <span className="ml-2 rounded-pill bg-accent-100 px-2 py-0.5 text-micro text-accent-700">
              шинэ
            </span>
          ) : null}
          {mine ? <span className="ml-2 text-micro text-accent-700">· таных</span> : null}
        </span>

        <span className="hidden shrink-0 text-meta text-ink-2 sm:block">{move}</span>

        {/* Цуглуулагчид алдааны хувь тусад нь — оноонд ОРООГҮЙ */}
        {!isBranch && entry.meta?.defectRate !== undefined ? (
          <span
            className={`num hidden shrink-0 text-meta sm:block ${
              entry.meta.defectRate > 0 ? 'text-alert-ink' : 'text-ink-3'
            }`}
          >
            {entry.meta.defectRate}% алдаа
          </span>
        ) : null}

        <span className="num shrink-0 text-h3">{Number(entry.totalScore).toFixed(0)}</span>

        <span aria-hidden className="shrink-0 text-micro text-ink-3">{open ? '▲' : '▼'}</span>
      </button>

      {/*
        «i» — өмнөх хугацаатай харьцуулалт. Мөрөнд бичвэл эрэмбэ
        уншигдахаа болино: тоо нь ил, учир шалтгаан нь энд.
      */}
      {prevScore !== null || prevTop ? (
        <InfoTip label={`${entry.subject.name} — өмнөх хугацаатай харьцуулалт`}>
          <span className="flex flex-col gap-1.5">
            <span className="block text-ink">
              {prevLabel ?? 'Өмнөх хугацаа'}-тай харьцуулбал
            </span>
            {prevScore === null ? (
              <span className="block">Өмнөх хугацаанд оролцоогүй — харьцуулах оноо алга.</span>
            ) : (
              <span className="block">
                Өөрийн оноо <b className="num">{prevScore.toFixed(0)}</b> →{' '}
                <b className="num">{Number(entry.totalScore).toFixed(0)}</b>
                {ownPct === null ? null : (
                  <>
                    {' ('}
                    <b className={ownPct >= 0 ? 'text-accent-700' : 'text-alert-ink'}>
                      {ownPct >= 0 ? '+' : ''}{ownPct}%
                    </b>
                    {')'}
                  </>
                )}
              </span>
            )}
            {prevTop ? (
              <span className="block border-t border-hair pt-1.5">
                Тэр үеийн шилдэг: <b>{prevTop.name}</b>{' '}
                <span className="num">{prevTop.score.toFixed(0)}</span> оноо.{' '}
                {vsTopPct === null ? null : vsTopPct >= 0 ? (
                  <b className="text-accent-700">Түүнээс {vsTopPct}% дээгүүр байна.</b>
                ) : (
                  <b className="text-ink-2">Түүнээс {Math.abs(vsTopPct)}% доогуур.</b>
                )}
              </span>
            ) : null}
          </span>
        </InfoTip>
      ) : null}
      </div>

      {open ? (
        <div className="flex flex-col gap-3 px-2 pt-1 pb-3.5">
          <ScoreTable rows={entry.breakdown} excluded={entry.excluded} />

          {isBranch ? (
            detail.isPending ? (
              <span className="text-meta text-ink-3">Задаргаа ачаалж байна…</span>
            ) : detail.error ? (
              <span className="text-meta text-alert-ink">
                {(detail.error as Error).message}
              </span>
            ) : detail.data && detail.data.data.contributors.length > 0 ? (
              <Contributors rows={detail.data.data.contributors} />
            ) : null
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function ScoreTable({ rows, excluded }: { rows: ScoreRow[]; excluded: string[] }) {
  const scale = rows.find((r) => r.scale !== undefined)?.scale;

  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((r) => (
        <div key={r.metric} className="flex flex-wrap items-baseline gap-x-2 text-meta">
          <span className="min-w-28 flex-1 text-ink-2 text-pretty">{r.label}</span>
          <span className="num text-ink-3">
            {r.normalized === undefined ? formatQty(r.raw) : `${r.normalized.toFixed(1)}`}
            {' × '}
            {r.weight}
            {r.scale === undefined ? '' : ` × ${r.scale}`}
          </span>
          <span
            className={`num w-14 shrink-0 text-right sm:w-[72px] ${
              Number(r.points) < 0 ? 'text-alert-ink' : 'text-ink'
            }`}
          >
            {Number(r.points).toFixed(1)}
          </span>
        </div>
      ))}

      {excluded.length > 0 ? (
        <span className="text-micro text-ink-3">
          Хамаараагүй: {excluded.join(', ')}
          {scale ? ` — үлдсэн жин ×${scale} болж дахин хуваарилагдсан` : ''}
        </span>
      ) : null}
    </div>
  );
}

/** ⚠️ ЗЭРЭГЛЭЛГҮЙ. Дугаар, байр, «шилдэг» гэсэн үг ХЭРЭГЛЭХГҮЙ. */
function Contributors({
  rows,
}: {
  rows: { id: string; name: string; revenue: string; units: number }[];
}) {
  return (
    <div className="flex flex-col gap-1.5 border-t border-hair pt-2.5">
      <span className="text-micro text-ink-3">
        Салбарын ажилтнуудын хувь нэмэр — зэрэглэлгүй, зөвхөн задаргаа
      </span>
      {rows.map((c) => (
        <div key={c.id} className="flex flex-wrap items-baseline gap-x-2 text-meta">
          <span className="min-w-28 flex-1 text-pretty">{c.name}</span>
          <span className="num text-ink-2">{formatQty(c.units)} ш</span>
          <span className="num w-20 shrink-0 text-right text-ink-2 sm:w-[96px]">
            {formatMnt(c.revenue)}
          </span>
        </div>
      ))}
    </div>
  );
}

function rankMove(e: LeaderboardEntry): string {
  if (e.previousRank === null) return 'шинэ';
  const d = e.previousRank - e.rank;
  if (d === 0) return '—';
  return d > 0 ? `▲${d}` : `▼${Math.abs(d)}`;
}
